import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Breadcrumbs,
  CircularProgress,
  Button,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Alert,
  Pagination,
  TextField,
  InputLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { formatDateTimeSecondsSk } from "app/utils/dateFormat";
import { getIntegrationServerLogs } from "app/services/api/integrationServerLogs";
import LogDetailModal from "./LogDetailModal";

const DEFAULT_LIMIT = 50;
const STATUS_OPTIONS = ["INFO", "WARNING", "ERROR", "DEBUG"];

const getStatusColor = (status) => {
  if (!status) return "default";
  const s = String(status).toUpperCase();
  if (s === "ERROR") return "error";
  if (s === "WARNING") return "warning";
  if (s === "INFO") return "info";
  if (s === "DEBUG") return "default";
  return "default";
};

const IntegrationServerLogsPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isUnderLogs = location.pathname.startsWith("/logs");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [page, setPage] = useState(1);

  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchText, setSearchText] = useState("");

  const [detailLog, setDetailLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    const offset = (page - 1) * limit;

    const createdFrom =
      dateFrom ? new Date(dateFrom).toISOString() : undefined;
    const createdTo = dateTo ? new Date(dateTo).toISOString() : undefined;

    try {
      const response = await getIntegrationServerLogs({
        limit,
        offset,
        entityType: entityType.trim() || undefined,
        action: action.trim() || undefined,
        status: status || undefined,
        createdFrom,
        createdTo,
      });

      const data = response?.data;
      const list = Array.isArray(data?.items) ? data.items : [];
      const totalCount = typeof data?.total === "number" ? data.total : list.length;

      setItems(list);
      setTotal(totalCount);
    } catch (err) {
      console.error("Integration server logs fetch error:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("pages.integrationServerLogs.errors.fetchFailed"),
      );
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, entityType, action, status, dateFrom, dateTo, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filteredBySearch = useMemo(() => {
    if (!searchText.trim()) return items;
    const q = searchText.trim().toLowerCase();
    return items.filter((log) => {
      const msg = (log.Message || "").toLowerCase();
      const act = (log.Action || "").toLowerCase();
      const et = (log.EntityType || "").toLowerCase();
      return msg.includes(q) || act.includes(q) || et.includes(q);
    });
  }, [items, searchText]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const hasActiveFilters = entityType || action || status || dateFrom || dateTo;

  const handleClearFilters = () => {
    setEntityType("");
    setAction("");
    setStatus("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const handleView = (log) => {
    setDetailLog(log);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setDetailLog(null);
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs
          sx={{
            "& a": { color: "text.secondary", textDecoration: "none" },
            "& a:hover": { textDecoration: "underline" },
          }}
        >
          {isUnderLogs ? (
            <Link to="/logs">{t("pages.logs.title")}</Link>
          ) : (
            <Link to="/customer/dsok">{t("sidebar.menuItem.dsokSettings")}</Link>
          )}
          <Typography color="text.primary">
            {t("pages.integrationServerLogs.title")}
          </Typography>
        </Breadcrumbs>
        <Typography variant="h2">{t("pages.integrationServerLogs.title")}</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexWrap="wrap"
          >
            <TextField
              size="small"
              label={t("pages.integrationServerLogs.filters.search")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t("pages.integrationServerLogs.filters.searchPlaceholder")}
              sx={{ minWidth: 180 }}
            />
            <TextField
              size="small"
              label={t("pages.integrationServerLogs.filters.entityType")}
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              sx={{ minWidth: 140 }}
            />
            <TextField
              size="small"
              label={t("pages.integrationServerLogs.filters.action")}
              value={action}
              onChange={(e) => setAction(e.target.value)}
              sx={{ minWidth: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>{t("pages.integrationServerLogs.filters.status")}</InputLabel>
              <Select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                label={t("pages.integrationServerLogs.filters.status")}
              >
                <MenuItem value="">{t("pages.integrationServerLogs.filters.all")}</MenuItem>
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {t(`pages.integrationServerLogs.status.${opt.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="datetime-local"
              label={t("pages.integrationServerLogs.filters.dateFrom")}
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              size="small"
              type="datetime-local"
              label={t("pages.integrationServerLogs.filters.dateTo")}
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            {hasActiveFilters && (
              <Button variant="text" size="small" onClick={handleClearFilters} disabled={loading}>
                {t("pages.integrationServerLogs.filters.clear")}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => (page === 1 ? fetchLogs() : setPage(1))}
              disabled={loading}
            >
              {t("pages.integrationServerLogs.filters.apply")}
            </Button>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select value={limit} onChange={handleLimitChange} disabled={loading}>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              disabled={loading}
            />
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchLogs}>
              {t("pages.integrationServerLogs.buttons.retry")}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("pages.integrationServerLogs.table.createdAt")}</TableCell>
                <TableCell>{t("pages.integrationServerLogs.table.status")}</TableCell>
                <TableCell>{t("pages.integrationServerLogs.table.entityType")}</TableCell>
                <TableCell>{t("pages.integrationServerLogs.table.action")}</TableCell>
                <TableCell>{t("pages.integrationServerLogs.table.message")}</TableCell>
                <TableCell align="right" width={80}>
                  {t("pages.integrationServerLogs.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : filteredBySearch.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t("pages.integrationServerLogs.empty.noData")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBySearch.map((log) => (
                  <TableRow key={log.LogID} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDateTimeSecondsSk(log.CreatedAt)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.Status}
                        color={getStatusColor(log.Status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{log.EntityType ?? "—"}</TableCell>
                    <TableCell>{log.Action ?? "—"}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 320,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {log.Message ?? "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("pages.integrationServerLogs.buttons.view")}>
                        <IconButton
                          size="small"
                          onClick={() => handleView(log)}
                          aria-label={t("pages.integrationServerLogs.buttons.view")}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <LogDetailModal
        open={detailOpen}
        onClose={handleCloseDetail}
        log={detailLog}
      />
    </Box>
  );
};

export default IntegrationServerLogsPage;
